import React, { useCallback, useState } from "react";
import { DocumentTextIcon, ChevronUpIcon, ChevronDownIcon, PencilIcon, DocumentDuplicateIcon } from "@heroicons/react/24/outline";
import TextEditor from "../../../Common/TextEditor/TinyEditor";
import api from "../../../../middleware/api";
import { useAuth } from "../../../../context/AuthContext";
import { toast } from "react-toastify";
import { CheckIcon, Loader2 } from "lucide-react";

const MailSignature = () => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [showTemplates, setShowTemplates] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const { user, setUser } = useAuth();

    const toggleExpansion = () => {
        setIsExpanded(!isExpanded);
    };

    const toggleEditing = () => {
        setIsEditing(!isEditing);
        setShowTemplates(false);
    };

    const toggleTemplates = () => {
        setShowTemplates(!showTemplates);
    };

    const handleSaveSignature = useCallback(async (content) => {
        setIsSaving(true);
        try {
            const response = await api.put(`/users/update/${user.id}/signature`, {
                signature: content
            });

            if (response.status === 200) {
                // Éviter les re-render inutiles qui peuvent réinitialiser la sélection
                if (user.signature !== content) {
                    setUser({ ...user, signature: content });
                }
                return true;
            }
            return false;
        } catch (error) {
            console.error("Erreur lors de la sauvegarde de la signature:", error);
            toast.error("Erreur lors de la sauvegarde de la signature");
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [user, setUser]);

    const applyTemplate = async (template) => {
        try {
            await handleSaveSignature(template);
            setShowTemplates(false);
            toast.success("Modèle de signature appliqué");
        } catch (error) {
            toast.error("Erreur lors de l'application du modèle");
        }
    };

    const signatureTemplates = [
        {
            id: 1,
            name: "Simple",
            template: `<p style="font-family: Arial, sans-serif; font-size: 14px; line-height: 1.4;">
                <strong>${user.firstname} ${user.lastname}</strong><br />
                Téléphone: ${user.phone || "+33 X XX XX XX XX"}<br />
                Email: <a href="mailto:${user.email.toLowerCase().trim()}" style="color: #4285f4;">${user.email.toLowerCase().trim()}</a>
            </p>`
        },
        {
            id: 2,
            name: "Professionnel",
            template: `<table style="font-family: Arial, sans-serif; font-size: 14px; color: #333333; max-width: 500px;">
                <tr>
                    <td style="vertical-align: top; padding-right: 15px; border-right: 2px solid #4285f4;">
                        <strong style="font-size: 16px; color: #4285f4;">${user.firstname} ${user.lastname}</strong><br />
                        <span style="color: #666666;">Titre professionnel</span><br />
                        <span style="color: #666666;">Entreprise</span>
                    </td>
                    <td style="vertical-align: top; padding-left: 15px;">
                        <span style="display: block; margin-bottom: 3px;">
                            <img src="https://cdn-icons-png.flaticon.com/16/455/455705.png" alt="phone" style="vertical-align: middle; margin-right: 5px;" /> 
                            ${user.phone || "+33 X XX XX XX XX"}
                        </span>
                        <span style="display: block; margin-bottom: 3px;">
                            <img src="https://cdn-icons-png.flaticon.com/16/552/552486.png" alt="email" style="vertical-align: middle; margin-right: 5px;" /> 
                            <a href="mailto:${user.email.toLowerCase().trim()}" style="color: #4285f4; text-decoration: none;">${user.email.toLowerCase().trim()}</a>
                        </span>
                        <span style="display: block; margin-bottom: 3px;">
                            <img src="https://cdn-icons-png.flaticon.com/16/1865/1865269.png" alt="web" style="vertical-align: middle; margin-right: 5px;" /> 
                            <a href="https://example.com" style="color: #4285f4; text-decoration: none;">www.example.com</a>
                        </span>
                    </td>
                </tr>
            </table>`
        }
    ];

    const renderSignaturePreview = () => {
        if (isSaving) return (
            <div className="bg-white border rounded-md p-4 mb-4">
                <div className="flex justify-between items-center mb-2">
                    <h3 className="text-lg font-medium">Prévisualisation</h3>
                </div>
                <div className="border-t pt-3 flex  items-center gap-x-2 lg:ml-1">
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    <span className="text-sm text-base-content/70">Enregistrement en cours...</span>
                </div>
            </div>
        );
        return (
            <div className="bg-white border rounded-md p-4 mb-4">
                <div className="flex justify-between items-center mb-2">
                    <h3 className="text-lg font-medium">Prévisualisation</h3>
                </div>
                <div className="border-t pt-3">
                    {user.signature ? (
                        <div dangerouslySetInnerHTML={{ __html: user.signature }} />
                    ) : (
                        <p className="text-gray-500 italic">Aucune signature configurée</p>
                    )}
                </div>
            </div>
        );
    };

    const renderTemplates = () => {
        return (
            <div className="bg-base-200 p-4 rounded-lg mb-4">
                <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-medium">Modèles de signature</h3>

                    <button
                        onClick={toggleTemplates}
                        className="btn btn-outline btn-ghost btn-sm"
                    >
                        Annuler
                    </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {signatureTemplates.map(template => (
                        <div
                            key={template.id}
                            className="bg-white p-3 rounded-md border hover:shadow-md transition-shadow cursor-pointer"
                            onClick={() => applyTemplate(template.template)}
                        >
                            <div className="flex justify-between items-center mb-2">
                                <h4 className="font-medium">{template.name}</h4>
                                <DocumentDuplicateIcon className="w-4 h-4 text-primary" />
                            </div>
                            <div className="border-t pt-2 max-h-32 overflow-hidden">
                                <div className="scale-75 origin-top-left" dangerouslySetInnerHTML={{ __html: template.template }} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <section aria-labelledby="mail-signature-title" className="bg-white rounded-lg shadow-md p-4 mt-4 w-full flex flex-col">
            <div
                className="flex justify-between items-center cursor-pointer"
                onClick={toggleExpansion}
            >
                <h2 id="mail-signature-title" className="text-xl font-semibold flex items-center gap-2">
                    <DocumentTextIcon className="w-5 h-5 text-primary" />
                    Signature Email
                </h2>
                <div className="flex items-center gap-2">
                    {!isEditing && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleEditing();
                            }}
                            className="btn btn-primary btn-xs h-8 min-h-0 flex items-center gap-1"
                            aria-label="Modifier la signature"
                        >
                            <PencilIcon className="w-3.5 h-3.5" />
                            {user.signature ? "Modifier" : "Créer"}
                        </button>
                    )}
                    {isExpanded ? (
                        <ChevronUpIcon className="w-5 h-5 text-gray-500" />
                    ) : (
                        <ChevronDownIcon className="w-5 h-5 text-gray-500" />
                    )}
                </div>
            </div>

            {isExpanded && (
                <div className="mt-3 flex-grow">
                    <p className="text-xs text-base-content/70 mb-3">
                        Personnalisez votre signature email qui sera utilisée dans vos communications.
                    </p>

                    {showTemplates && renderTemplates()}

                    {isEditing && !showTemplates && (
                        <>
                            <div className="flex flex-row gap-2 w-full items-center justify-between mb-3">
                                <div className="flex justify-end ">
                                    <button
                                        onClick={toggleTemplates}
                                        className="btn btn-outline btn-ghost btn-sm flex items-center gap-1"
                                    >
                                        <DocumentDuplicateIcon className="w-4 h-4" />
                                        Utiliser un modèle
                                    </button>
                                </div>
                                <div className="flex ">
                                    <button
                                        onClick={toggleEditing}
                                        className="btn btn-primary btn-sm"
                                    >
                                        <CheckIcon className="w-4 h-4" />
                                        Enregistrer
                                    </button>
                                </div>


                            </div>
                            <div className="min-h-[350px] flex">
                                <TextEditor
                                    initialValue={user.signature || ""}
                                    onSave={handleSaveSignature}
                                    showSaveIndicator={true}
                                    debounceTime={0}
                                    containerHeight='350px'
                                    editorHeight='350px'
                                />
                            </div>
                        </>
                    )}

                    {!isEditing && !showTemplates && renderSignaturePreview()}
                </div>
            )}
        </section>
    );
};

export default MailSignature; 